import { useEffect, useState, useCallback } from "react";
import { Download, Target, Send, BellRing } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

function SegmentNotifyPanel({ segment, value, count, segmentLabel }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/marketing/notify", { segment, value: value || null, title, message });
      toast.success(`Notification envoyée à ${r.data.sent} client(s)`);
      setTitle(""); setMessage(""); setConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi");
    } finally { setSending(false); }
  };

  const ready = title.trim() && message.trim() && count > 0;

  return (
    <div className="ic-card !transform-none p-5 mb-8" data-testid="marketing-notify-panel">
      <h2 className="font-serif-elegant text-xl font-bold flex items-center gap-2 mb-1"><BellRing size={18} className="text-[#D4AF37]" /> Notification ciblée</h2>
      <p className="text-xs text-gray-500 mb-4">Envoyée dans l'app uniquement aux clients du segment sélectionné : <span className="text-[#D4AF37]">{segmentLabel}</span> · {count} destinataire(s)</p>
      <div className="grid gap-3 md:grid-cols-[1fr_2fr]">
        <input data-testid="notify-title" className="ic-input" placeholder="Titre (ex : -20% ce jeudi soir)" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} />
        <textarea data-testid="notify-message" className="ic-input min-h-[44px]" rows={2} placeholder="Message de l'offre…" value={message} onChange={(e) => setMessage(e.target.value)} maxLength={400} />
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-4">
        {!confirm ? (
          <button data-testid="notify-send-btn" onClick={() => setConfirm(true)} disabled={!ready} className="btn-gold !py-2 text-sm disabled:opacity-40">
            <Send size={15} /> Envoyer à {count} client(s)
          </button>
        ) : (
          <>
            <span className="text-sm text-gray-300" data-testid="notify-confirm-text">Confirmer l'envoi à {count} client(s) ?</span>
            <button data-testid="notify-confirm-btn" onClick={send} disabled={sending} className="btn-gold !py-2 text-sm disabled:opacity-40">{sending ? "Envoi…" : "Oui, envoyer"}</button>
            <button data-testid="notify-cancel-btn" onClick={() => setConfirm(false)} className="btn-outline-gold !py-2 text-sm">Annuler</button>
          </>
        )}
      </div>
    </div>
  );
}

const SEGMENTS = [
  { value: "all", label: "Tous les clients" },
  { value: "email", label: "Consentement email" },
  { value: "sms", label: "Consentement SMS" },
  { value: "new", label: "Nouveaux clients (X jours)" },
  { value: "ritas", label: "Solde RITAS ≥ X" },
  { value: "inactive", label: "Inactifs depuis X jours" },
  { value: "event", label: "Ont réservé une soirée" },
];

const DEFAULTS = { new: "30", ritas: "100", inactive: "60" };

export default function AdminMarketing() {
  const [segment, setSegment] = useState("all");
  const [value, setValue] = useState("");
  const [events, setEvents] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { segment };
    if (value) params.value = value;
    api.get("/admin/marketing/contacts", { params }).then((r) => setContacts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [segment, value]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const changeSegment = (s) => {
    setSegment(s);
    setValue(DEFAULTS[s] || "");
  };

  const exportCsv = () => {
    const rows = [["Prénom", "Nom", "Email", "Téléphone", "Solde RITAS", "Consent. email", "Consent. SMS", "Inscription"],
      ...contacts.map((c) => [c.first_name, c.last_name, c.email, c.phone || "", c.balance, c.consent_email ? "oui" : "non", c.consent_sms ? "oui" : "non", c.created_at?.split("T")[0] || ""])];
    const csv = rows.map((r) => r.map((v) => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(";")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = `marketing-${segment}-italian-club.csv`;
    a.click();
  };

  const needsValue = ["new", "ritas", "inactive"].includes(segment);

  return (
    <div data-testid="admin-marketing">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <h1 className="font-serif-elegant text-3xl font-bold flex items-center gap-3"><Target size={26} className="text-[#D4AF37]" /> Offres & Marketing</h1>
        <button data-testid="marketing-export-btn" onClick={exportCsv} disabled={contacts.length === 0} className="btn-outline-gold !py-2 text-sm disabled:opacity-40">
          <Download size={15} /> Exporter CSV ({contacts.length})
        </button>
      </div>

      <div className="ic-card !transform-none p-5 mb-8 flex flex-wrap items-end gap-4">
        <div>
          <label className="text-xs text-gray-500 block mb-1">Segment de clients</label>
          <select data-testid="marketing-segment" className="ic-input !w-auto" value={segment} onChange={(e) => changeSegment(e.target.value)}>
            {SEGMENTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        {needsValue && (
          <div>
            <label className="text-xs text-gray-500 block mb-1">{segment === "ritas" ? "Solde minimum" : "Nombre de jours"}</label>
            <input data-testid="marketing-value" type="number" min="1" className="ic-input !w-32" value={value} onChange={(e) => setValue(e.target.value)} />
          </div>
        )}
        {segment === "event" && (
          <div>
            <label className="text-xs text-gray-500 block mb-1">Soirée</label>
            <select data-testid="marketing-event" className="ic-input !w-auto" value={value} onChange={(e) => setValue(e.target.value)}>
              <option value="">— Choisir —</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.date} · {ev.title}</option>)}
            </select>
          </div>
        )}
        <p className="text-sm text-gray-400 ml-auto" data-testid="marketing-count">{loading ? "Chargement…" : `${contacts.length} client(s) ciblé(s)`}</p>
      </div>

      <SegmentNotifyPanel segment={segment} value={value} count={contacts.length} segmentLabel={SEGMENTS.find((s) => s.value === segment)?.label} />

      <div className="ic-card !transform-none overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-left">
            <th className="p-4 gold-label">Client</th><th className="p-4 gold-label">Email</th><th className="p-4 gold-label">Téléphone</th>
            <th className="p-4 gold-label">RITAS</th><th className="p-4 gold-label">Consentements</th>
          </tr></thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-b border-white/5" data-testid={`marketing-row-${c.id}`}>
                <td className="p-4 font-medium">{c.first_name} {c.last_name}</td>
                <td className="p-4 text-gray-400">{c.email}</td>
                <td className="p-4 text-gray-400">{c.phone || "—"}</td>
                <td className="p-4 text-[#D4AF37]">{c.balance}</td>
                <td className="p-4 text-xs">
                  <span className={c.consent_email ? "text-[#009246]" : "text-gray-600"}>Email</span>{" · "}
                  <span className={c.consent_sms ? "text-[#009246]" : "text-gray-600"}>SMS</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {contacts.length === 0 && !loading && <p className="p-6 text-gray-500">Aucun client dans ce segment.</p>}
      </div>
      <p className="text-xs text-gray-600 mt-3">Conformité RGPD : n'utilisez les segments « email » et « SMS » que pour les envois marketing — seuls les clients ayant donné leur consentement y figurent.</p>
    </div>
  );
}
